import { useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  motion,
  useScroll,
  useTransform,
  useReducedMotion,
} from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import { HERO } from '@/data/marketing/content';
import { HERO_BG } from '@/data/marketing/photos';
import { useIsMobile } from '@/hooks/use-mobile';
import { staggerContainer, fadeUp, heroTitleWord } from '@/lib/animations';

/**
 * Hero plein écran immersif (style Viture) :
 * - Photo full-bleed avec overlay sombre pour lisibilité
 * - Cercles décoratifs cuivre (gardés de l'ancien Hero)
 * - Texte qui se révèle en stagger à l'arrivée :
 *   eyebrow → titre (mot par mot) → sous-titre → CTAs
 * - Flèche scroll en bas qui bounce
 */
export default function HeroImmersive() {
  const titleStartWords = HERO.titleStart.trim().split(/\s+/);
  const titleAccentWords = HERO.titleAccent.trim().split(/\s+/);

  const sectionRef = useRef<HTMLElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  // Court-circuit aussi sur mobile : effet à peine perceptible et coûteux.
  const disableScrollFx = prefersReducedMotion || isMobile;
  // Parallax : la photo descend plus lentement que le scroll (effet profondeur)
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  // Court-circuite les transforms scroll-driven si l'utilisateur préfère
  // moins d'animations (a11y WCAG 2.3.3 + économie batterie mobile).
  const bgY = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? ['0%', '0%'] : ['0%', '25%']
  );
  const bgScale = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? [1, 1] : [1, 1.15]
  );
  const contentOpacity = useTransform(
    scrollYProgress,
    [0, 0.6],
    disableScrollFx ? [1, 1] : [1, 0]
  );
  const contentY = useTransform(
    scrollYProgress,
    [0, 1],
    disableScrollFx ? ['0px', '0px'] : ['0px', '-80px']
  );

  return (
    <section
      ref={sectionRef}
      className="relative h-[100svh] min-h-[640px] overflow-hidden"
    >
      {/* ─── Background photo plein écran (parallax + scale au scroll) ─── */}
      <motion.img
        src={HERO_BG}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        initial={{ scale: 1.08, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
      />

      {/* Overlay : assombrit le bas + dégradé latéral pour le texte */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/85 via-[#0A0A0A]/50 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/30 via-transparent to-[#0A0A0A]" />

      {/* Cercles décoratifs cuivre */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full border border-[#D4A574]/15" />
      <div className="pointer-events-none absolute -top-20 -right-12 h-[300px] w-[300px] rounded-full border border-[#D4A574]/20" />
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[400px] w-[400px] rounded-full border border-[#D4A574]/15" />

      {/* ─── Contenu texte centré-gauche (fade-out doux au scroll) ─── */}
      <motion.div
        className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 h-full flex items-center"
        initial="hidden"
        animate="visible"
        variants={staggerContainer(0.18, 0.3)}
        style={{ opacity: contentOpacity, y: contentY }}
      >
        <div className="max-w-3xl">
          {/* Eyebrow */}
          <motion.div variants={fadeUp}>
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#D4A574]">
              <span className="h-px w-8 bg-[#D4A574]/40" />
              {HERO.eyebrow}
            </div>
          </motion.div>

          {/* Titre — chaque mot anime séparément */}
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[1.05] mt-6 text-[#E8E0D6]">
            <motion.span
              className="block"
              variants={staggerContainer(0.08, 0.1)}
            >
              {titleStartWords.map((word, i) => (
                <motion.span
                  key={i}
                  variants={heroTitleWord}
                  className="inline-block mr-[0.25em]"
                >
                  {word}
                </motion.span>
              ))}
            </motion.span>
            <motion.span
              className="block italic text-[#D4A574]"
              variants={staggerContainer(0.08, 0.3)}
            >
              {titleAccentWords.map((word, i) => (
                <motion.span
                  key={i}
                  variants={heroTitleWord}
                  className="inline-block mr-[0.25em]"
                >
                  {word}
                </motion.span>
              ))}
            </motion.span>
          </h1>

          {/* Sous-titre */}
          <motion.p
            variants={fadeUp}
            className="mt-8 text-base md:text-lg text-neutral-200 leading-relaxed max-w-2xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
          >
            {HERO.subtitle}
          </motion.p>

          {/* CTAs */}
          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-wrap gap-3"
          >
            <Link
              to={HERO.ctaPrimary.href}
              className="group inline-flex items-center gap-2 px-6 py-3 rounded-md bg-[#D4A574] text-[#0A0A0A] font-medium text-sm tracking-wide hover:bg-[#C9A36B] transition-all shadow-2xl shadow-[#D4A574]/20"
            >
              {HERO.ctaPrimary.label}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
            <a
              href={HERO.ctaSecondary.href}
              className="inline-flex items-center px-6 py-3 rounded-md border border-[#E8E0D6]/30 backdrop-blur-sm bg-[#0A0A0A]/30 text-[#E8E0D6] font-medium text-sm tracking-wide hover:bg-[#1A1A1A]/60 hover:border-[#D4A574]/60 transition-all"
            >
              {HERO.ctaSecondary.label}
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* ─── Indicateur scroll en bas (bounce) ─── */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#D4A574]/70"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.6 }}
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Découvrir</span>
        <motion.div
          animate={prefersReducedMotion ? undefined : { y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ChevronDown className="h-5 w-5" />
        </motion.div>
      </motion.div>
    </section>
  );
}
